import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@csa/web-foundation";
import { useT } from "../i18n/index";
import { usePartyPrincipal } from "../auth/party-principal";
import { Icon } from "../ui/icons";
import { ROUTES } from "./nav";

// The authenticated customer chrome — Turkish-first, on the founder's design language:
// a persistent LEFT sidebar (brand · overview nav with icons + active states · the
// party's user chip pinned at the foot) and a full-width content column with a calm
// top bar (page context + TR/EN). On narrow widths the sidebar collapses to an
// off-canvas drawer toggled from the top bar. No grant gating — the party has none.
export function CustomerShell({ children }: { children: ReactNode }) {
  const { t, locale, setLocale } = useT();
  const { logout } = useAuth();
  const { profile, isLoading } = usePartyPrincipal();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  const name = profile
    ? `${profile.firstName} ${profile.lastName}`
    : isLoading
      ? t("shell.loading")
      : "";
  const initials = profile
    ? `${profile.firstName[0] ?? ""}${profile.lastName[0] ?? ""}`.toUpperCase()
    : "";
  const active = ROUTES.find((r) => location.pathname.startsWith(r.path));

  return (
    <div className="cx-shell" data-open={navOpen}>
      <aside className="cx-sidebar">
        <div className="cx-brand">
          <span className="cx-brand__mark" aria-hidden="true">
            <Icon name="protection" size={22} strokeWidth={1.9} />
          </span>
          <span className="cx-brand__text">
            <span className="cx-brand__name">{t("app.title")}</span>
            <span className="cx-brand__tag">{t("app.tagline")}</span>
          </span>
        </div>

        <nav aria-label="customer" className="cx-nav">
          <p className="cx-nav__section">{t("nav.section")}</p>
          <ul>
            {ROUTES.map((r) => (
              <li key={r.key}>
                <NavLink
                  to={r.path}
                  className={`cx-nav__item${r.primary ? " cx-nav__cta" : ""}`}
                  onClick={() => setNavOpen(false)}
                >
                  <Icon name={r.icon} size={19} />
                  <span>{t(r.labelKey)}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="cx-sidebar__foot">
          <div className="cx-userchip">
            <span className="cx-avatar" aria-hidden="true">
              {initials}
            </span>
            <span className="cx-userchip__meta">
              <span className="cx-userchip__name" data-testid="party-name">
                {name}
              </span>
              <span className="cx-userchip__role">{t("shell.role")}</span>
            </span>
          </div>
          <button
            type="button"
            className="cx-iconbtn"
            aria-label={t("auth.logout")}
            title={t("auth.logout")}
            onClick={() => void logout()}
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </aside>

      {navOpen ? (
        <button
          type="button"
          className="cx-scrim"
          aria-label={t("shell.closeNav")}
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <div className="cx-content">
        <header className="cx-topbar">
          <button
            type="button"
            className="cx-iconbtn cx-menu-btn"
            aria-label={t("shell.openNav")}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon name="menu" size={20} />
          </button>
          <span className="cx-topbar__crumb">
            <span className="cx-topbar__tag muted">{t("app.tagline")} · </span>
            {active ? t(active.labelKey) : t("nav.home")}
          </span>
          <span className="cx-spacer" />
          <div className="cx-langtoggle" role="group" aria-label={t("set.langTitle")}>
            <button
              type="button"
              className={locale === "tr" ? "is-active" : ""}
              aria-pressed={locale === "tr"}
              onClick={() => setLocale("tr")}
            >
              TR
            </button>
            <button
              type="button"
              className={locale === "en" ? "is-active" : ""}
              aria-pressed={locale === "en"}
              onClick={() => setLocale("en")}
            >
              EN
            </button>
          </div>
        </header>
        <main className="cx-main">{children}</main>
      </div>
    </div>
  );
}
